import React, { useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Stack,
  Typography,
  Chip,
  alpha,
} from "@mui/material";
import {
  Gavel as GavelIcon,
  Checklist as ChecklistIcon,
  Psychology as MentalIcon,
} from "@mui/icons-material";

import {
  ConsentReasons,
  DecisionMakers,
} from "../../components/ReferenceData/MentalCapacity";

const MentalCapacity = () => {
  const [activeTab, setActiveTab] = useState(0);

  const tabConfigs = [
    {
      label: "Consent Reasons",
      icon: <ChecklistIcon sx={{ fontSize: 16 }} />,
      component: <ConsentReasons />,
    },
    {
      label: "Decision Makers",
      icon: <GavelIcon sx={{ fontSize: 16 }} />,
      component: <DecisionMakers />,
    },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const TabPanel = ({ children, value, index }) => (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`mental-capacity-tabpanel-${index}`}
      aria-labelledby={`mental-capacity-tab-${index}`}
    >
      {value === index && (
        <Box
          sx={{
            animation: "fadeIn 0.2s ease-in",
            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "translateY(6px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      {/* PAGE HEADER */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "10px",
              bgcolor: alpha("#2469F0", 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MentalIcon sx={{ color: "#2469F0", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#0f172a" }}>
              Mental Capacity
            </Typography>
            <Typography
              variant="caption"
              color="#64748b"
              sx={{ fontSize: "0.75rem" }}
            >
              Manage consent reasons and decision maker roles
            </Typography>
          </Box>
        </Stack>

        <Chip
          label={`${tabConfigs.length} sections`}
          size="small"
          sx={{
            height: 22,
            fontSize: "0.65rem",
            fontWeight: 600,
            bgcolor: alpha("#2469F0", 0.08),
            color: "#2469F0",
          }}
        />
      </Box>

      {/* TABS PAPER */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid #E2E8F0",
          bgcolor: "#FFFFFF",
        }}
      >
        {/* TABS HEADER */}
        <Box
          sx={{
            bgcolor: "#F8FAFC",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="Mental capacity sections"
            variant="standard"
            sx={{
              minHeight: 56,
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: 3,
                backgroundColor: "#2469F0",
              },
              "& .MuiTab-root": {
                minHeight: 56,
                fontSize: "0.85rem",
                fontWeight: 500,
                textTransform: "none",
                color: "#64748B",
                transition: "all 0.15s ease-in-out",
                px: 3,
                "&:hover": {
                  color: "#2469F0",
                  bgcolor: alpha("#2469F0", 0.04),
                },
                "&.Mui-selected": {
                  color: "#2469F0",
                  fontWeight: 600,
                },
                "& .MuiTab-iconWrapper": {
                  marginRight: 1,
                },
              },
            }}
          >
            {tabConfigs.map((tab, index) => (
              <Tab
                key={tab.label}
                icon={tab.icon}
                label={tab.label}
                iconPosition="start"
                id={`mental-capacity-tab-${index}`}
                aria-controls={`mental-capacity-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>

        {/* TAB CONTENT */}
        <Box sx={{ p: { xs: 2, md: 2.5 } }}>
          {tabConfigs.map((tab, index) => (
            <TabPanel key={tab.label} value={activeTab} index={index}>
              {tab.component}
            </TabPanel>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default MentalCapacity;